<template>
    <v-offline @detected-condition="toggleOnline" />
</template>

<script>
import { VOffline } from 'v-offline';
import { createNamespacedHelpers } from 'vuex';

import { handleResponseError } from '@/Helpers.js';
import axios from '@/plugins/axios.js';
import initialLoadErrorGate from '@/services/InitialLoadErrorGate.js';

const { mapMutations } = createNamespacedHelpers('Online');
const { mapState } = createNamespacedHelpers('Auth');

export default {
    name: 'OnlineStatus',

    components: {
        VOffline,
    },

    emits: ['ready', 'online-check'],

    data: () => ({
        offlineNotification: null,
        intervalChecker: null,
        attempt: 0,
    }),

    computed: {
        ...mapState({ user: state => state.user }),
    },

    watch: {
        async user(user) {
            if (user) {
                await this.initializeForUser(user);
            }
        },
    },

    async created() {
        // Fix for timing issue: If user is already available when component is created,
        // the watcher won't fire, so we need to check for user here too
        if (this.user) {
            await this.initializeForUser(this.user);
        }
    },

    beforeUnmount() {
        this.$echo.leave('online');

        clearInterval(this.intervalChecker);
    },

    methods: {
        ...mapMutations({
            syncOnlineUsers: 'SYNC_ONLINE_USERS',
        }),

        async initializeForUser(user) {
            this.$store.commit('Online/SET_READY_LISTENER', () => {
                this.$emit('ready');

                this.$echo
                    .private(`notifications-${user.id}`)
                    .listen('ConversationMessageNotification', async ({ conversation, sender }) => {
                        // A message the user sent never alerts them, even if the server sends one.
                        if (sender && Number(sender.id) === Number(user.id)) return;

                        if (!this.$store.getters['Conversation/conversation'](conversation.id)) {
                            await this.$store.dispatch('Conversation/unknownConversation', conversation.id);
                        }

                        const sound = new Audio('/notification.mp3');

                        try {
                            await sound.play();
                            // eslint-disable-next-line no-empty
                        } catch (e) {}

                        const originalTitle = document.title;

                        document.title = `New message received... ${originalTitle}`;

                        setTimeout(() => (document.title = originalTitle), 5000);
                    });
            });

            await this.startChecking();

            await this.$store.dispatch('Online/createWebsocketConnection', this.$echo);
        },

        async startChecking() {
            const check = async () => {
                try {
                    const { data } = await axios.get('/api/online', {
                        errorHandle: false,
                    });

                    this.$store.commit('Online/SYNC_ONLINE_USERS', data.data);

                    initialLoadErrorGate.markReady();

                    this.$emit('online-check', data);
                } catch (error) {
                    if (!initialLoadErrorGate.shouldSuppressConnectivityUserError(error)) {
                        handleResponseError(error);
                    }
                }
            };

            const timer = this.$store.getters['InstitutionSettings/get']('online_interval_check') || 30;

            await check();
            this.intervalChecker = setInterval(check, timer * 1000);
        },

        toggleOnline(isOnline) {
            if (isOnline) {
                if (this.offlineNotification) {
                    this.offlineNotification.close();
                    this.offlineNotification = null;
                }

                return;
            }

            // Mid-quiz the quiz-taking UI owns failed-save recovery (the inline
            // "Your answer could not be saved." + Retry). This global snackbar would
            // both duplicate that message and offer a page "Reload" that discards the
            // student's in-progress attempt state — suppress it during an active
            // attempt (DEFECT-4 / AC-5).
            if (this.user?.quiz_active_id) {
                return;
            }

            if (initialLoadErrorGate.shouldSuppressOfflineSnackbar()) {
                return;
            }

            this.offlineNotification = this.$snackbar.open({
                type: 'is-warning',
                message: 'It appears the network is offline.',
                position: 'is-bottom-right',
                indefinite: true,
                actionText: 'Reload',
                onAction: () => window.location.reload(),
            });
        },
    },
};
</script>
